import { useReactiveVar } from '@apollo/client';
import { type FC, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { justPlacedOnHoldVar } from '../../contexts/fill';
import {
    useAvailablePathways,
    useFillPathway,
    useFillPrescription,
} from '../../contexts/fill/use-fill-prescription';
import { CoverageStatus } from '../../graphql/generated/graphql';
import { usePathwayOverride } from '../../hooks/use-pathway-override';
import { usePathwayScreen } from '../../hooks/use-pathway-screen';
import { useProgramTerms } from '../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../util/get-absolute-route';

export const AutoDetectPathway: FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const justPlacedOnHold = useReactiveVar(justPlacedOnHoldVar);

    const { drx, drxId, medication } = useFillPrescription();
    const { pathway, setPathway } = useFillPathway();
    const currentScreen = usePathwayScreen();
    const availablePathways = useAvailablePathways();
    const insuranceTerms = useProgramTerms(medication, 'insurance');

    const pathwayOverride = usePathwayOverride(medication);

    const { coverageInfo } = drx;
    const coverageStatus =
        coverageInfo?.coverageStatus ?? CoverageStatus.Unknown;
    const hasInsurance = !!coverageInfo?.hasInsurance;
    const forceCoveredPathway =
        hasInsurance &&
        insuranceTerms?.savingsCard?.insuranceRequirement === 'ANY_COMMERCIAL';

    useEffect(() => {
        if (drx?.status === 'ON_HOLD') {
            if (currentScreen === 'on-hold' || justPlacedOnHold) {
                return;
            }
            navigate(
                {
                    pathname: getAbsoluteRoute('fill.drxId.onHold', {
                        drxId,
                    }),
                },
                { replace: true }
            );

            return;
        }

        // Skip detection. We're already on a pathway, either because it was auto-detected or the user chose it.
        if (!drx || pathway) {
            return;
        }

        if (
            pathwayOverride?.initialPathway &&
            availablePathways.includes(pathwayOverride.initialPathway)
        ) {
            if (pathwayOverride.initialScreen === 'path-select') {
                navigate(
                    {
                        pathname: getAbsoluteRoute('fill.drxId.pathSelect', {
                            drxId,
                        }),
                        search: `?pathway=${pathwayOverride.initialPathway}`,
                    },
                    { replace: true }
                );
            } else if (pathwayOverride.initialScreen === 'pharmacy-select') {
                navigate(
                    {
                        pathname: getAbsoluteRoute('fill.drxId.pharmacy.root', {
                            drxId,
                        }),
                        search: `?pathway=${pathwayOverride.initialPathway}`,
                    },
                    { replace: true }
                );
            } else {
                setPathway({
                    pathway: pathwayOverride.initialPathway,
                    isUserOverride: false,
                });
            }
            return;
        }

        if (availablePathways.length === 1) {
            setPathway({
                pathway: availablePathways[0],
                isUserOverride: false,
            });
            return;
        }

        if (coverageStatus === CoverageStatus.PriorAuthRequired) {
            if (
                availablePathways.includes('insurance') &&
                availablePathways.includes('self-pay')
            ) {
                navigate(
                    {
                        pathname: getAbsoluteRoute('fill.drxId.pathSelect', {
                            drxId,
                        }),
                        search: `?pathway=insurance`,
                    },
                    { replace: true }
                );
                return;
            } else if (availablePathways.includes('insurance')) {
                setPathway({ pathway: 'insurance', isUserOverride: false });
                return;
            }
        }

        if (
            (forceCoveredPathway ||
                coverageStatus === CoverageStatus.Covered ||
                coverageStatus === CoverageStatus.CoveredRestrictions) &&
            availablePathways.includes('insurance')
        ) {
            setPathway({ pathway: 'insurance', isUserOverride: false });
            return;
        }

        if (coverageStatus === CoverageStatus.Unknown) {
            setPathway({ pathway: 'fallback', isUserOverride: false });
            return;
        }

        if (availablePathways.includes('self-pay')) {
            setPathway({ pathway: 'self-pay', isUserOverride: false });
            return;
        }

        setPathway({ pathway: 'fallback', isUserOverride: false });
    }, [
        availablePathways,
        coverageInfo,
        coverageStatus,
        drx,
        drxId,
        forceCoveredPathway,
        navigate,
        pathway,
        pathwayOverride,
        currentScreen,
        searchParams,
        setPathway,
        justPlacedOnHold,
    ]);

    return <></>;
};
